// src/components/BlogList.jsx
import React, { useState, useEffect } from 'react';
import CommonHeader from './CommonHeader';
import CommonFooter from './CommonFooter';
import config from '../config';
import './BlogList.css';

const BlogList = ({ onBlogClick, onBackToHome, onLoginClick, onSignupClick }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5;

  useEffect(() => {
    fetchBlogs();
  }, []);


  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    setSubscribeError('');

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/subscribe/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribed(true);
        setEmail('');
        setTimeout(() => setSubscribed(false), 3000);
      } else {
        setSubscribeError(data.error || data.message || 'Subscription failed. Please try again.');
        setTimeout(() => setSubscribeError(''), 3000);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setSubscribeError('Network error. Please try again.');
      setTimeout(() => setSubscribeError(''), 3000);
    } finally {
      setSubscribing(false);
    }
  };



  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/api/blog/`);
      if (!response.ok) throw new Error('Failed to fetch blogs');
      const data = await response.json();
      setPosts(data);

      const tags = new Set();
      data.forEach(post => {
        if (post.tags) {
          post.tags.split(',').forEach(tag => tags.add(tag.trim()));
        }
      });
      setAllTags(Array.from(tags));
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  // Complete navigation handler
  const handleNavigate = (page) => {
    switch (page) {
      case 'home':
        if (onBackToHome) {
          onBackToHome();
        }
        break;
      case 'features':
        if (onBackToHome) {
          onBackToHome();
          setTimeout(() => {
            const featuresSection = document.getElementById('features');
            if (featuresSection) {
              featuresSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case 'how-it-works':
        if (onBackToHome) {
          onBackToHome();
          setTimeout(() => {
            const howItWorksSection = document.getElementById('how-it-works');
            if (howItWorksSection) {
              howItWorksSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case 'pricing':
        if (onBackToHome) {
          onBackToHome();
          setTimeout(() => {
            const pricingSection = document.getElementById('pricing');
            if (pricingSection) {
              pricingSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case 'blog':
        break;
      default:
        if (onBackToHome) {
          onBackToHome();
        }
        break;
    }
  };

  // Filter posts based on search and tags
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = selectedTag === '' || (post.tags && post.tags.includes(selectedTag));
    return matchesSearch && matchesTag;
  });

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Go to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedTag]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading stories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h3>Something went wrong</h3>
        <p>{error}</p>
        <button onClick={fetchBlogs} className="retry-btn">Try Again</button>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <CommonHeader
        activePage="blog"
        onNavigate={handleNavigate}
        onLogin={onLoginClick}
        onSignup={onSignupClick}
      />

      {/* Main Content with Sidebar */}
      <div className="blog-main-content">
        <div className="container">
          <div className="content-layout">
            {/* Main Content - Blog Posts Grid */}
            <div className="blog-posts-section">
              {filteredPosts.length === 0 ? (
                <div className="no-posts">
                  <div className="no-posts-icon">📭</div>
                  <h3>No articles found</h3>
                  <p>Try adjusting your search or filter criteria</p>
                </div>
              ) : (
                <>
                  <div className="results-count">
                    Showing {indexOfFirstPost + 1} to {Math.min(indexOfLastPost, filteredPosts.length)} of {filteredPosts.length} articles
                  </div>

                  <div className="posts-grid">
                    {currentPosts.map(post => (
                      <article key={post.id} className="post-card" onClick={() => onBlogClick(post.slug)}>
                        {post.featured_image && (
                          <div className="post-image">
                            <img src={post.featured_image} alt={post.title} />
                          </div>
                        )}
                        <div className="post-content">
                          <div className="post-meta">
                            <span className="post-date">{post.formatted_date}</span>
                            <span className="post-read-time">{post.read_time} min read</span>
                          </div>
                          <h2 className="post-title">{post.title}</h2>
                          <p className="post-excerpt">{post.excerpt}</p>
                          <div className="post-footer">
                            <span className="post-author">By {post.author}</span>
                            <span className="read-more">Read More →</span>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>

                  {/* Pagination Component */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        className="pagination-btn prev-btn"
                      >
                        ← Previous
                      </button>

                      <div className="pagination-numbers">
                        {[...Array(totalPages)].map((_, index) => (
                          <button
                            key={index + 1}
                            onClick={() => paginate(index + 1)}
                            className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                          >
                            {index + 1}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        className="pagination-btn next-btn"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar - Search and Tags */}
            <aside className="blog-sidebar">
              {/* Search Widget */}
              <div className="sidebar-widget search-widget">
                <h3>🔍 Search Articles</h3>
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search by title or content..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="sidebar-search-input"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Categories/Tags Widget */}
              {allTags.length > 0 && (
                <div className="sidebar-widget tags-widget">
                  <h3>🏷️ Popular Tags</h3>
                  <div className="tags-list">
                    <button
                      className={`tag-item ${selectedTag === '' ? 'active' : ''}`}
                      onClick={() => setSelectedTag('')}
                    >
                      All Posts
                      <span className="tag-count">{posts.length}</span>
                    </button>
                    {allTags
                      .map(tag => ({
                        tag: tag,
                        count: posts.filter(post => post.tags && post.tags.includes(tag)).length
                      }))
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 10)
                      .map(({ tag, count }) => (
                        <button
                          key={tag}
                          className={`tag-item ${selectedTag === tag ? 'active' : ''}`}
                          onClick={() => setSelectedTag(tag)}
                        >
                          {tag}
                          <span className="tag-count">{count}</span>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Newsletter Widget - Stay Updated */}
              <div className="sidebar-widget newsletter-widget">
                <h3>📧 Stay Updated</h3>
                <p>Get the latest articles delivered to your inbox</p>

                {subscribeError && (
                  <div className="subscribe-error">
                    <span>⚠️</span> {subscribeError}
                  </div>
                )}

                {subscribed ? (
                  <div className="subscribe-success">
                    <span>✓</span>
                    <p>Thanks for subscribing!</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="newsletter-form">
                    <input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={subscribing}
                      className="newsletter-input"
                    />
                    <button type="submit" className="subscribe-btn" disabled={subscribing}>
                      {subscribing ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </form>
                )}
                <p className="newsletter-note">No spam, unsubscribe anytime.</p>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <CommonFooter onNavigate={handleNavigate} activePage="blog" />
    </div>
  );
};

export default BlogList;